import { useApprovalFlowViewModel } from "../viewmodel/approvalFlowViewModel";
import { useApprovalViewModel } from "../viewmodel/approvalViewModel";
import {
  setApprovalFlowName,
  setApprovalFlowLevels,
  setApprovalFlowIsDefault,
  setApprovalFlowNumLevels,
  updateApprovalLevel,
  resetApprovalFlow,
  hideToast,
  setToast,
  setLoading,
} from "../viewmodel/approvalFlow.action";
import {
  updateApprovalLevel as updateApprovalLevelApi,
  updateApprovals,
} from "../api/ApprovalFlow.api";

import DefaultModal from "../component/dialog";
import { PoolModelData } from "../model/approvalPoolModel";
import Toast from "../../../../../components/Toast";
import {
  ApprovalFlowDetailsData,
  ApprovalFlowGetData,
  ApprovalLevels,
  ApprovalLevelType,
} from "../model/approvalsFlowModel";
import { useEffect } from "react";
import SelectdDropDown from "../../../../../components/DropDownComponent";
import { FallBackType, ManagerReferenceType } from "../model/jobApprovalEnums";
import { XMarkIcon } from "@heroicons/react/24/outline";
import InputField from "../../../../../components/InputFieldForm";

interface CreateApprovalFlowDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (flow: Omit<ApprovalFlowDetailsData, "id" | "createdAt">) => void;
  existingFlow?: ApprovalFlowGetData;
  approvalVM: ReturnType<typeof useApprovalViewModel>;
  approvalFlowVM: ReturnType<typeof useApprovalFlowViewModel>;
  onSaveSuccess: () => Promise<void>;
  mode: "create" | "edit" | "view";
  detailedFlow?: ApprovalFlowDetailsData;
}

export const CreateApprovalFlowDialog = ({
  open,
  onOpenChange,
  existingFlow,
  approvalVM,
  approvalFlowVM,
  mode,
  detailedFlow,
}: CreateApprovalFlowDialogProps) => {
  const ManagerReferenceTypeMap: Record<ManagerReferenceType, string> = {
    [ManagerReferenceType.HiringManager]: "Hiring Manager",
    [ManagerReferenceType.JobCreater]: "Job Creator",
    [ManagerReferenceType.Recruiter]: "Recruiter",
  };
  const FallBackMap: Record<FallBackType, string> = {
    [FallBackType.Admin]: "Admin",
    [FallBackType.Pool]: "Pool",
  };
  const pools: PoolModelData[] = approvalVM.state.Pools;
  const users = approvalVM.state.users || [];
  const isView = mode === "view";

  const { state: flowState, dispatch } = useApprovalFlowViewModel(
    {
      name: existingFlow?.name || "",
      isDefault: existingFlow?.is_default || false,
      numLevels: existingFlow?.total_levels || 0,
    },
    { autoFetch: false },
  );
  const getDialogTitle = () => {
    if (mode === "create") {
      return "Create Approval Flow";
    }

    if (mode === "edit") {
      return existingFlow?.name
        ? `Edit Approval Flow – ${existingFlow.name}`
        : "Edit Approval Flow";
    }

    if (mode === "view") {
      return detailedFlow?.name
        ? `Approval Flow – ${detailedFlow.name}`
        : "Approval Flow";
    }

    return "Approval Flow";
  };

  const { name, levels, isDefault, numLevels } = flowState;

  const handleNumLevelsChange = (value: string) => {
    const num = parseInt(value) || 0;
    dispatch(setApprovalFlowNumLevels(num));
    if (num > levels.length) {
      const newLevels: ApprovalLevels[] = [...levels];
      for (let i = levels.length; i < num; i++) {
        newLevels.push({
          id: Math.random().toString(),
          level_order: i + 1,
          approver_type: "" as ApprovalLevelType,
          use_job_creator: false,
          reference_company_user_id: null,
          reference_company_user_name: null,
          company_user_id: null,
          company_user_name: null,
          pool_id: null,
          pool_name: null,
          min_approvals: 1,
          pool_members_count: 0,
          manager_reference_type: null,
          fallback_type: null,
          fallback_pool_id: null,
        });
      }
      console.log("NEW LEVELS:", newLevels);
      dispatch(setApprovalFlowLevels(newLevels));
    } else if (num < levels.length) {
      dispatch(setApprovalFlowLevels(levels.slice(0, num)));
    }
  };

  const apiLevelToUiLevel = (lvl: ApprovalLevels): ApprovalLevels => ({
    id: lvl.id,
    level_order: lvl.level_order,
    approver_type: lvl.approver_type,
    use_job_creator: lvl.use_job_creator ?? false,
    reference_company_user_id: lvl.reference_company_user_id ?? null,
    reference_company_user_name: lvl.reference_company_user_name ?? null,
    company_user_id: lvl.company_user_id ?? null,
    company_user_name: lvl.company_user_name ?? null,
    pool_id: lvl.pool_id ?? null,
    pool_name: lvl.pool_name ?? null,
    min_approvals: lvl.min_approvals ?? 1,
    pool_members_count: lvl.pool_members_count ?? 0,
    manager_reference_type: lvl.manager_reference_type ?? null,
    fallback_type: lvl.fallback_type ?? null,
    fallback_pool_id: lvl.fallback_pool_id ?? null,
  });

  useEffect(() => {
    if (!open) return;
    if (mode === "create") {
      dispatch(resetApprovalFlow());
      return;
    }

    if ((mode === "edit" || mode === "view") && detailedFlow) {
      dispatch(setApprovalFlowName(detailedFlow.name));
      dispatch(setApprovalFlowIsDefault(detailedFlow.is_default));
      dispatch(setApprovalFlowNumLevels(detailedFlow.total_levels));

      dispatch(
        setApprovalFlowLevels(detailedFlow.levels.map(apiLevelToUiLevel)),
      );
    }
  }, [mode, existingFlow, detailedFlow, open]);

  const updateLevel = (id: string, updates: Partial<ApprovalLevels>) => {
    dispatch(updateApprovalLevel(id, updates));
  };

  const buildLevelPayload = (level: ApprovalLevels, idx: number) => ({
    level_order: idx + 1,
    approver_type: level.approver_type,
    manager_reference_type:
      level.approver_type === "manager"
        ? level.manager_reference_type ?? null
        : null,
    use_job_creator:
      level.approver_type === "manager"
        ? level.use_job_creator ?? false
        : false,
    reference_company_user_id: level.reference_company_user_id ?? null,
    fallback_type:
      level.approver_type === "manager"
        ? level.fallback_type ?? "none"
        : "none",
    fallback_pool_id: level.fallback_pool_id ?? null,
    company_user_id:
      level.approver_type === "single_person"
        ? level.company_user_id ?? null
        : null,
    pool_id: level.approver_type === "pool" ? level.pool_id ?? null : null,
    min_approvals:
      level.approver_type === "pool" ? level.min_approvals ?? 1 : 1,
    escalation_enabled: true,
    escalation_hours: 48,
  });

  const handleSave = async () => {
    dispatch(setLoading(true));
    try {
      if (mode === "edit" && detailedFlow?.id) {
        console.log("[EDIT] detailedFlow:", detailedFlow);
        console.log("[EDIT] levels in state:", levels);

        // API 1: update flow metadata
        await updateApprovals(detailedFlow.id, {
          name,
          description: detailedFlow.description ?? "",
          is_default: isDefault,
          is_active: true,
        });
        console.log("[EDIT] API 1 done - flow metadata updated");

        // API 2: update each level using real IDs from detailedFlow
        const levelUpdates = detailedFlow.levels.map((originalLevel) => {
          const updatedLevel =
            levels.find((l) => l.id === originalLevel.id) ?? originalLevel;
          const idx = detailedFlow.levels.indexOf(originalLevel);
          const payload = buildLevelPayload(updatedLevel, idx);
          console.log(
            `[EDIT] calling updateApprovalLevelApi for level ${originalLevel.id}`,
            payload,
          );
          return updateApprovalLevelApi(
            detailedFlow.id,
            originalLevel.id,
            payload,
          );
        });
        await Promise.all(levelUpdates);
        console.log("[EDIT] API 2 done - all levels updated");

        dispatch(
          setToast({
            isVisible: true,
            type: "success",
            message: "Approval flow updated successfully",
          }),
        );
      } else {
        const createPayload = {
          name,
          description: "",
          is_default: isDefault,

          levels: levels.map((level, idx) => ({
            level_order: idx + 1,
            approver_type: level.approver_type,
            escalation_hours: 48,
            escalation_enabled: true,
            ...(level.approver_type === "manager" && {
              manager_reference_type:
                level.manager_reference_type ??
                ManagerReferenceType.HiringManager,
              fallback_type: level.fallback_type,
              fallback_pool_id: level.fallback_pool_id,
            }),
            ...(level.approver_type === "single_person" && {
              company_user_id: level.company_user_id,
            }),
            ...(level.approver_type === "pool" && {
              pool_id: level.pool_id,
              min_approvals: level.min_approvals || 1,
            }),
          })),
        };
        console.log("FINAL PAYLOAD", createPayload);
        await approvalFlowVM.createApprovalFlow(createPayload);
        dispatch(
          setToast({
            isVisible: true,
            type: "success",
            message: "Approval flow created successfully",
          }),
        );
      }
      // Fetch updated approval flows after save
      if (typeof approvalFlowVM.fetchApprovals === "function") {
        await approvalFlowVM.fetchApprovals();
      }
      onOpenChange(false);
      dispatch(resetApprovalFlow());
    } catch (err: any) {
      console.error("Save failed", err);
      console.error("err.message:", err?.message);
      console.error("err.response?.data:", err?.response?.data);
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to save approval flow";
      dispatch(
        setToast({
          isVisible: true,
          type: "error",
          message: errorMessage,
        }),
      );
    } finally {
      dispatch(setLoading(false));
    }
  };
  const sortedUsers = [...users].sort((a, b) =>
    (a.full_name || "").localeCompare(b.full_name || ""),
  );
  const ApprovalTypeLabelMap: Record<ApprovalLevelType, string> = {
    manager: "Manager",
    single_person: "Single Person",
    pool: "Pool Name",
  };
  const viewInputClass =
    "bg-transparent border-0 shadow-none p-0 text-gray-900 pointer-events-none appearance-none";
  return (
    <div>
      <DefaultModal
        open={open}
        onClose={() => onOpenChange(false)}
        className="w-full max-w-full sm:max-w-2xl lg:max-w-4xl mx-2 sm:mx-auto"
      >
        <div className="flex flex-col max-h-[90vh] sm:max-h-[85vh]">
          <div className="flex items-center justify-between px-4 py-4">
            <h3 className="text-lg font-medium text-gray-900">
              {getDialogTitle()}

              <p className="text-gray-600 text-sm">
                Configure the approval levels for this workflow.
              </p>
            </h3>
            <button
              onClick={() => {
                onOpenChange(false);
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 sm:p-4 space-y-3">
            {/* Name */}
            <div>
              <InputField
                disabled={isView}
                type="text"
                label="Approval Flow Name"
                value={name}
                placeholder="e.g., Standard Approval"
                onChange={(e) => dispatch(setApprovalFlowName(e.target.value))}
                className={`w-full ${
                  isView ? viewInputClass : "border rounded px-3 py-2"
                }`}
              ></InputField>
            </div>

            {/* Number of levels */}

            <div className="flex flex-col ">
              <InputField
                type="number"
                label=" Number of Approval Levels"
                min="1"
                disabled={isView || mode === "edit"}
                className={`w-full ${
                  isView || mode === "edit"
                    ? viewInputClass
                    : "border rounded px-3 py-2"
                }`}
                placeholder="Enter levels"
                value={numLevels || ""}
                onChange={(e) => handleNumLevelsChange(e.target.value)}
              ></InputField>
            </div>

            {/* LEVELS */}
            {numLevels > 0 && (
              <div className="space-y-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Configure Approval Levels
                </label>

                {levels.map((level, index) => (
                  <div
                    key={level.id}
                    className="border rounded-lg p-3 sm:p-4 space-y-3"
                  >
                    <div className="font-medium">Level {index + 1}</div>

                    {/* TYPE SELECT */}
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Type
                      </label>
                      {isView ? (
                        <p>
                          {level.approver_type
                            ? ApprovalTypeLabelMap[level.approver_type]
                            : "-"}
                        </p>
                      ) : (
                        <select
                          disabled={isView}
                          value={level.approver_type || ""}
                          onChange={(e) =>
                            updateLevel(level.id, {
                              approver_type: e.target
                                .value as ApprovalLevelType,
                              manager_reference_type:
                                e.target.value === "manager"
                                  ? ManagerReferenceType.HiringManager
                                  : null,
                              fallback_type: null,
                              fallback_pool_id: null,
                              company_user_name: undefined,
                              pool_id: undefined,
                              pool_name: undefined,
                              min_approvals: undefined,
                            })
                          }
                          className="border p-2 rounded w-full"
                        >
                          {" "}
                          <option value="">Select Type</option>
                          <option value="manager">Manager</option>
                          <option value="single_person">Single Person</option>
                          <option value="pool">Pool Name</option>
                        </select>
                      )}
                    </div>
                    {/* MANAGER */}
                    {level.approver_type === "manager" && (
                      <div className="space-y-1">
                        <SelectdDropDown
                          id="who_s_manager"
                          label="Who’s Manager"
                          viewMode={isView}
                          value={level.manager_reference_type || ""}
                          options={Object.values(ManagerReferenceType).map(
                            (type) => ({
                              label: ManagerReferenceTypeMap[type],
                              value: type,
                            }),
                          )}
                          onChange={(e) => {
                            updateLevel(level.id, {
                              manager_reference_type: e,
                            });
                          }}
                        />
                      </div>
                    )}
                    {level.approver_type === "manager" && (
                      <div className="space-y-1">
                        <SelectdDropDown
                          viewMode={isView}
                          value={level.fallback_type || ""}
                          label="Fall Back  (If user does’t have manager)"
                          placeholder="select fall back"
                          onChange={(e) => {
                            updateLevel(level.id, { fallback_type: e });
                          }}
                          options={Object.values(FallBackType).map((type) => ({
                            label: FallBackMap[type],
                            value: type,
                          }))}
                          id="fallback_type"
                        ></SelectdDropDown>
                      </div>
                    )}
                    {level.approver_type === "manager" &&
                      level.fallback_type === FallBackType.Pool && (
                        <div className="space-y-1">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Select Pool
                          </label>
                          {isView ? (
                            <p>{level.fallback_type}</p>
                          ) : (
                            <select
                              value={level.fallback_pool_id || ""}
                              onChange={(e) => {
                                const pool = pools.find(
                                  (p) => p.id === e.target.value,
                                );

                                updateLevel(level.id, {
                                  fallback_pool_id: e.target.value,
                                  //fallback_pool_name: pool?.name || null,
                                });
                              }}
                              className="border p-2 rounded w-full"
                            >
                              <option value="">Select pool</option>
                              {pools.map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.name}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      )}

                    {/* SINGLE PERSON */}
                    {level.approver_type === "single_person" && (
                      <div className="space-y-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Select User
                        </label>
                        {isView ? (
                          <p>{level.company_user_name}</p>
                        ) : (
                          <select
                            disabled={isView}
                            value={level.company_user_id || ""}
                            onChange={(e) => {
                              const user = users.find(
                                (u) => u.id === e.target.value,
                              );
                              updateLevel(level.id, {
                                company_user_id: e.target.value,
                                company_user_name: user?.name,
                              });
                            }}
                            className="border p-2 rounded w-full"
                          >
                            <option value="">Choose user</option>
                            {sortedUsers.map((u) => (
                              <option key={u.id} value={u.id}>
                                {u.full_name}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    )}

                    {/* POOL */}
                    {level.approver_type === "pool" && (
                      <div className="space-y-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Select Pool
                        </label>
                        {isView ? (
                          <p>{level.pool_name}</p>
                        ) : (
                          <select
                            disabled={isView}
                            value={level.pool_id || ""}
                            onChange={(e) => {
                              const pool = pools.find(
                                (p) => p.id === e.target.value,
                              );
                              updateLevel(level.id, {
                                pool_id: e.target.value,
                                pool_name: pool?.name,
                                pool_members_count: pool?.member_count || 0,
                              });
                            }}
                            className="border p-2 rounded w-full"
                          >
                            <option value="">No Pools available</option>
                            {pools.map((p: PoolModelData) => (
                              <option key={p.id} value={p.id}>
                                {p.name}
                              </option>
                            ))}
                          </select>
                        )}
                        <div className="space-y-1">
                          <label className="text-sm font-medium">
                            Number of approvals
                          </label>
                          <input
                            type="number"
                            min={1}
                            className={`w-full ${
                              isView
                                ? viewInputClass
                                : "border rounded px-3 py-2"
                            }`}
                            max={level.pool_members_count ?? undefined}
                            disabled={!level.pool_id || isView}
                            value={level.min_approvals ?? 0}
                            onChange={(e) => {
                              const raw = parseInt(e.target.value, 10);
                              const maxVal =
                                typeof level.pool_members_count === "number"
                                  ? level.pool_members_count
                                  : Infinity;
                              let val = Number.isNaN(raw) ? 1 : raw;
                              if (val < 1) val = 1;
                              if (val > maxVal) val = maxVal;
                              updateLevel(level.id, { min_approvals: val });
                            }}
                          />
                          {!level.pool_id && (
                            <div className="text-xs text-gray-500">
                              Select a pool to set number of approvals
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Missing Approval/ Un Responded Approval escalated in 48 hours to
                the manager
              </label>
            </div>
            {/* DEFAULT CHECKBOX */}
            <div className="flex items-start sm:items-center gap-2">
              <input
                disabled={isView}
                id="default-flow"
                type="checkbox"
                checked={isDefault}
                onChange={(e) =>
                  dispatch(setApprovalFlowIsDefault(e.target.checked))
                }
                className="w-4 h-4"
              />
              <label htmlFor="default-flow" className="text-sm">
                Set as default approval flow
              </label>
            </div>
          </div>

          <div
            className="
  sticky bottom-0
  bg-white
  p-4
  border-t
  flex
  flex-col
  sm:flex-row
  gap-3
  sm:justify-end
  "
          >
            <button
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              onClick={() => {
                dispatch(resetApprovalFlow());
                onOpenChange(false);
              }}
            >
              {mode === "view" ? "Close" : "Cancel"}
            </button>

            {mode !== "view" && (
              <button
                disabled={!name || !levels.length}
                className={`px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed ${
                  !name || !levels.length
                    ? "bg-indigo-300 cursor-not-allowed"
                    : "bg-indigo-600 hover:bg-indigo-700"
                }`}
                onClick={handleSave}
              >
                {flowState.loading && (
                  <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                )}

                {mode === "edit"
                  ? flowState.loading
                    ? "Updating..."
                    : "Update Approval Flow"
                  : flowState.loading
                  ? "Creating..."
                  : "Create Approval Flow"}
              </button>
            )}
          </div>
        </div>
      </DefaultModal>
      <Toast
        message={flowState.toast.message}
        type={flowState.toast.type}
        isVisible={flowState.toast.isVisible}
        onClose={() => dispatch(hideToast())}
      />
    </div>
  );
};
